import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';

import { useGetLimuloGamesQuery } from '@/services/limuloApi';
import {
  useGetLimuloMyLeaguePositionQuery,
  useGetLimuloSeasonLeaderboardQuery,
  useGetLimuloSeasonsQuery,
} from '@/services/limuloLeghe';
import {
  legheInAttesa,
  legheInCorso,
  meseDellaLega,
  stagioniSenzaGioco,
  ultimoIstante,
  type LegaRiga,
  type LegaStagione,
} from '@/lib/limulo/leghe';
import { limuloClashRoyaleGame } from '@/lib/limulo/profiles';
import { SlotPelleLeghe } from '@/lib/limulo/slotPelleLeghe';
import { limuloFailure } from '@/lib/limulo/errors';
import { LimuloDegraded } from '@/routes/limulo/LimuloDegraded';
import { SectionCard } from '@/components/hub/SectionCard';
import { EmptyState } from '@/components/common/EmptyState';
import { PanelWaiting } from '@/components/hub/PanelNotice';
import { listVerdict } from '@/lib/listState';
import { useStalled, useEverFailed, NO_ANSWER_MS } from '@/hooks/useStalled';
import { cn } from '@/lib/cn';

/**
 * La scheda «Rankings» della Hub CR quando `VITE_FEATURE_LEGHE_ENABLED` e' acceso: la LEGA DEL
 * MESE di qLeagues al posto delle stagioni Laravel (Luca, I-224 → L-2312 (a); solo staging per
 * L-2313 (a)). Dati, rotte, busta e la trappola degli id: `lib/limulo/leghe.ts`.
 *
 * I casi, ognuno con la SUA frase (la regola della Hub: un vuoto si afferma solo a risposta
 * ricevuta, e un errore dice quale domanda non ha avuto risposta):
 *  - nessuna lega in corso per il gioco ⇒ lo dice il server, e lo diciamo;
 *  - la lega del mese esiste ma non e' partita (`legheInAttesa`) ⇒ lo diciamo, senza dire perche';
 *  - ci sono leghe in corso che non si attribuiscono a nessun gioco ⇒ il vuoto NON si afferma;
 *  - Clash Royale non e' nel catalogo ⇒ la domanda non e' nemmeno partita: non e' un vuoto;
 *  - la classifica e' vuota ⇒ «ancora nessuno in classifica», non «e' partita»: qLeagues risponde
 *    vuoto anche a una lega non partita (`vista.js:207-208`);
 *  - un rifiuto del BFF (424 senza provider, 403 endpoint spento, 401) ⇒ `LimuloDegraded`, che lo
 *    NOMINA: per un 424 o un 403 riprovare non serve, e «impossibile caricare» inviterebbe a farlo.
 *    La frase nostra resta per cio' che il BFF non ha detto: nessuna risposta, o una forma ignota.
 */
export function LeghePanel() {
  const { t, i18n } = useTranslation('leaderboard');
  const giochi = useGetLimuloGamesQuery(undefined);
  const stagioni = useGetLimuloSeasonsQuery(undefined, { refetchOnMountOrArgChange: 60 });
  // L'istante della prima resa basta: serve solo a separare «il mese e' aperto» da «e' il mese dopo».
  // ⚠️ Con la scheda aperta a cavallo della mezzanotte del primo non si ricalcola: caso limite accettato.
  const [adesso] = useState(() => Date.now());
  const Pelle = useContext(SlotPelleLeghe);

  const tutte = stagioni.data?.items ?? [];
  const cr = limuloClashRoyaleGame(giochi.data);
  const leghe = legheInCorso(tutte, cr);

  const giochiAnswered = giochi.data !== undefined;
  const giochiFailed = useEverFailed(giochi.isError, giochiAnswered);
  // Un corpo senza `data.items` e' una forma che non conosciamo (`paginaOf` → null): non e' un vuoto.
  const stagioniAnswered = stagioni.data !== undefined;
  const stagioniFailed = useEverFailed(stagioni.isError, stagioniAnswered) || stagioni.data === null;
  const pending = !(giochiAnswered && stagioniAnswered) && !(giochiFailed || stagioniFailed);
  const stalled = useStalled(pending);
  const noAnswer = useStalled(pending, NO_ANSWER_MS);
  const verdict = listVerdict(leghe.length, [
    {
      reachable: true,
      loading: giochi.isLoading,
      slow: stalled,
      failed: giochiFailed || noAnswer,
      answered: giochiAnswered,
    },
    {
      reachable: true,
      loading: stagioni.isLoading,
      slow: stalled,
      failed: stagioniFailed || noAnswer,
      answered: stagioniAnswered,
    },
  ]);

  if (verdict === 'loading' || verdict === 'slow') {
    return (
      <div className="flex h-32 items-center justify-center">
        <PanelWaiting slow={verdict === 'slow'} />
      </div>
    );
  }
  if (verdict === 'empty') {
    // Il catalogo ha risposto ma Clash Royale non c'e': la domanda non e' nemmeno partita.
    if (cr === null) return <Avviso>{t('leghe.no_game')}</Avviso>;
    const inAttesa = legheInAttesa(tutte, cr, adesso);
    if (inAttesa.length > 0) {
      return <EmptyState>{t('leghe.waiting_start', { month: etichettaMese(inAttesa[0], i18n.language) })}</EmptyState>;
    }
    // Leghe in corso che non so attribuire: quella di CR potrebbe essere fra loro.
    if (stagioniSenzaGioco(tutte, giochi.data ?? []).length > 0) return <Avviso>{t('leghe.unmatched')}</Avviso>;
    return <EmptyState>{t('leghe.empty')}</EmptyState>;
  }
  if (verdict !== 'rows') {
    // ⚠️ `error`, NON `isError ? error`: durante una rilettura (focus, riconnessione) RTK rimette
    // `isError` a false e LASCIA `error` — che solo un `fulfilled` cancella. Leggendo `isError` il
    // verdetto nominato lampeggiava su «impossibile caricare» a ogni ritorno sull'app (revisione di
    // #458, punto 10): e' il segnale non monotono che «Gli 8 tab» di CLAUDE.md vieta.
    const rifiuto = stagioni.error ?? giochi.error;
    return rifiuto !== undefined && !noAnswer ? (
      <LimuloDegraded failure={limuloFailure(rifiuto)} context="list" />
    ) : (
      <Avviso errore>{t('leghe.load_error')}</Avviso>
    );
  }

  if (Pelle?.Classifica) return <Pelle.Classifica leghe={leghe} Lega={ClassificaLega} />;

  return (
    <SectionCard flush className="flex min-h-0 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto overscroll-contain p-3 [touch-action:pan-y]">
        {leghe.map((lega) => (
          <ClassificaLega key={lega.id} lega={lega} conModalita={leghe.length > 1} />
        ))}
      </div>
    </SectionCard>
  );
}

function Avviso({ children, errore = false }: { children: string; errore?: boolean }) {
  return (
    <p
      className={cn(
        'card-edge rounded-xl bg-bg-secondary p-6 text-center text-sm',
        errore ? 'text-danger' : 'text-fg-muted',
      )}
    >
      {children}
    </p>
  );
}

function etichettaMese(lega: LegaStagione, lang: string): string {
  const m = meseDellaLega(lega);
  return m
    ? new Date(Date.UTC(m.anno, m.mese - 1, 1)).toLocaleDateString(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' })
    : lega.title;
}

function etichettaFine(lega: LegaStagione, lang: string): string | null {
  const ultimo = ultimoIstante(lega);
  return ultimo === null ? null : new Date(ultimo).toLocaleDateString(lang, { day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** Una riga «e' mia» se il server ha detto chi sono e combacia la chiave (`entityId` = sub in qLeagues). */
function eMia(r: LegaRiga, io: LegaRiga | null): boolean {
  if (!io) return false;
  return (io.entityId !== '' && r.entityId === io.entityId) || (io.userId !== '' && r.userId === io.userId);
}

function ClassificaLega({ lega, conModalita }: { lega: LegaStagione; conModalita: boolean }) {
  const { t, i18n } = useTranslation('leaderboard');
  const Pelle = useContext(SlotPelleLeghe);
  const q = useGetLimuloSeasonLeaderboardQuery(lega.seasonId, { refetchOnMountOrArgChange: 60 });
  const mia = useGetLimuloMyLeaguePositionQuery(lega.seasonId, { refetchOnMountOrArgChange: 60 });
  const righe = q.data?.items ?? [];

  const answered = q.data !== undefined;
  // Forma ignota (`null`) = non so: e' un errore, non «ancora nessuno in classifica».
  const failed = useEverFailed(q.isError, answered) || q.data === null;
  const pending = !answered && !failed;
  const stalled = useStalled(pending);
  const noAnswer = useStalled(pending, NO_ANSWER_MS);
  const verdict = listVerdict(righe.length, [
    { reachable: true, loading: q.isLoading, slow: stalled, failed: failed || noAnswer, answered },
  ]);

  const fine = etichettaFine(lega, i18n.language);
  // `mia.data`: undefined = non ha ancora risposto · null = forma ignota · `{riga: null}` = nessuna posizione.
  const io = mia.data?.riga ?? null;
  const ioInLista = righe.some((r) => eMia(r, io));
  // `q.error`, non `q.isError`: v. sopra — durante una rilettura `isError` e' falso ed `error` resta.
  const rifiuto = !noAnswer ? q.error : undefined;
  if (Pelle?.Lega)
    return (
      <Pelle.Lega
        vista={{
          lega,
          conModalita,
          verdict,
          righe,
          mie: righe.map((r) => eMia(r, io)),
          io,
          ioInLista,
          mia: mia.data,
          rifiuto,
          totale: q.data?.total ?? null,
        }}
      />
    );

  return (
    <section className="flex flex-col gap-2">
      <header className="flex items-baseline justify-between gap-3 px-1">
        <h3 className="truncate text-sm font-bold text-fg">
          {t('leghe.title', { month: etichettaMese(lega, i18n.language) })}
          {conModalita && lega.metadata.gameModeKey ? ` · ${lega.metadata.gameModeKey}` : ''}
        </h3>
        {fine && <span className="shrink-0 text-xs text-fg-muted">{t('leghe.until', { date: fine })}</span>}
      </header>

      {verdict === 'loading' || verdict === 'slow' ? (
        <PanelWaiting slow={verdict === 'slow'} />
      ) : verdict === 'empty' ? (
        <p className="rounded-xl bg-bg-secondary p-4 text-center text-sm text-fg-muted">{t('leghe.ranking_empty')}</p>
      ) : verdict !== 'rows' ? (
        rifiuto !== undefined ? (
          <LimuloDegraded failure={limuloFailure(rifiuto)} context="standings" />
        ) : (
          <p className="rounded-xl bg-bg-secondary p-4 text-center text-sm text-danger">{t('leghe.ranking_error')}</p>
        )
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-bg-secondary">
          {righe.map((r) => {
            const mine = eMia(r, io);
            return (
              <li
                key={r.id}
                data-mia={mine ? 'true' : undefined}
                className={cn('flex items-center gap-3 px-4 py-2.5', mine && 'bg-brand/15')}
              >
                <span className="w-7 text-sm tabular-nums text-fg-muted">{r.rank}</span>
                <span className={cn('flex-1 truncate text-sm', mine ? 'font-semibold text-brand' : 'text-fg')}>
                  {r.displayName || t('player_fallback')}
                </span>
                {mine && <Trophy aria-hidden className="h-3.5 w-3.5 text-brand" />}
                <span className="text-sm font-semibold tabular-nums text-brand">
                  {t('leghe.points', { n: r.score })}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* La riga «tu» esiste solo se il server ha RISPOSTO su di me con una forma nota: in attesa, in
          errore o su una forma ignota non si afferma niente. `{riga: null}` e' una risposta — «non hai
          ancora punti in questa lega». */}
      {verdict === 'rows' && mia.data && !ioInLista && (
        <div
          data-testid="leghe-tu"
          className="flex shrink-0 items-center gap-3 rounded-xl border border-brand/40 bg-brand/15 px-4 py-2.5"
        >
          {io ? (
            <>
              <span className="w-7 text-sm font-semibold tabular-nums text-fg-muted">{io.rank}</span>
              <span className="flex-1 truncate text-sm font-semibold text-brand">{t('leghe.you')}</span>
              <span className="text-sm font-semibold tabular-nums text-brand">{t('leghe.points', { n: io.score })}</span>
            </>
          ) : (
            <span className="flex-1 text-sm text-fg-muted">{t('leghe.not_ranked')}</span>
          )}
        </div>
      )}
    </section>
  );
}
